import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Sync as SyncIcon,
  SyncDisabled as SyncDisabledIcon,
  CloudDownload as CloudDownloadIcon,
  DeleteSweep as DeleteSweepIcon,
  Refresh as RefreshIcon,
  SignalWifi4Bar as SignalWifi4BarIcon,
  SignalWifi3Bar as SignalWifi3BarIcon,
  SignalWifi2Bar as SignalWifi2BarIcon,
  SignalWifi1Bar as SignalWifi1BarIcon,
  SignalWifiOff as SignalWifiOffIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const SyncStatusIndicator = ({
  syncStatus = 'idle',
  lastSyncTime,
  retryCount = 0,
  networkQuality = 100,
  cacheSize = 0,
  isOnline = true,
  isOffline = false,
  isSyncing = false,
  hasRecentData = true,
  onForceSync,
  onClearCache,
  onReconnect,
  compact = false,
  customTheme,
  showDetails = true
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pulseAnimation, setPulseAnimation] = useState(false);
  
  // Efeito para animação quando há sincronização
  useEffect(() => {
    if (isSyncing) {
      setPulseAnimation(true);
      const timer = setTimeout(() => setPulseAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSyncing]);

  // Determina o ícone de qualidade da rede
  const getNetworkIcon = () => {
    if (isOffline) return SignalWifiOffIcon;
    if (networkQuality >= 80) return SignalWifi4BarIcon;
    if (networkQuality >= 60) return SignalWifi3BarIcon;
    if (networkQuality >= 40) return SignalWifi2BarIcon;
    if (networkQuality >= 20) return SignalWifi1BarIcon;
    return SignalWifiOffIcon;
  };

  // Determina cor do status
  const getStatusColor = () => {
    const activeTheme = customTheme || theme;
    
    if (isSyncing) return activeTheme.palette.info.main;
    if (isOffline && !hasRecentData) return activeTheme.palette.error.main;
    if (isOffline && hasRecentData) return activeTheme.palette.warning.main;
    if (isOnline) return activeTheme.palette.success.main;
    return activeTheme.palette.text.secondary;
  };

  // Formata o tamanho do cache
  const formatCacheSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Formata tempo da última sincronização
  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Nunca';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h atrás`;
    return date.toLocaleDateString();
  };

  // Obtém o texto do status
  const getStatusText = () => {
    if (isSyncing) return 'Sincronizando...';
    if (isOffline && !hasRecentData) return 'Offline';
    if (isOffline && hasRecentData) return 'Cache';
    if (isOnline) return 'Online';
    return 'Conectando...';
  };

  // Obtém tooltip detalhado
  const getTooltipTitle = () => {
    const status = getStatusText();
    const lastSync = formatLastSync(lastSyncTime);
    const cache = formatCacheSize(cacheSize);
    const quality = `${networkQuality}%`;
    
    return `Status: ${status} • Última sincronização: ${lastSync} • Cache: ${cache} • Rede: ${quality}`;
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    switch (action) {
      case 'sync':
        onForceSync?.();
        break;
      case 'reconnect':
        onReconnect?.();
        break;
      case 'clearCache':
        onClearCache?.();
        break;
      default:
        break;
    }
  };

  const NetworkIcon = getNetworkIcon();
  const statusColor = getStatusColor();

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {retryCount > 0 && (
          <Chip
            size="small"
            label={`${retryCount}x`}
            color="warning"
            variant="outlined"
            sx={{ 
              height: 16,
              fontSize: '8px',
              '& .MuiChip-label': {
                px: 0.5
              }
            }}
          />
        )}
      </Box>
    );
  }

  // Versão detalhada (não compacta)
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(statusColor, 0.05),
        border: `1px solid ${alpha(statusColor, 0.2)}`
      }}
    >
      {/* Indicador principal */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Badge
          variant="dot"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: statusColor,
              color: statusColor,
              animation: isSyncing || pulseAnimation ? 'pulse 1.5s infinite' : 'none'
            }
          }}
        >
          <NetworkIcon 
            sx={{ 
              color: statusColor, 
              fontSize: 32,
              animation: pulseAnimation ? 'glow 1s ease-in-out' : 'none'
            }} 
          />
        </Badge>
        
        <Box>
          <Typography variant="h6" sx={{ color: statusColor, fontWeight: 600 }}>
            {getStatusText()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Qualidade: {networkQuality}% • Cache: {formatCacheSize(cacheSize)}
          </Typography>
        </Box>
      </Box>

      {/* Barra de progresso durante sincronização */}
      {isSyncing && (
        <LinearProgress 
          sx={{ 
            width: '100%', 
            borderRadius: 1,
            '& .MuiLinearProgress-bar': {
              bgcolor: statusColor
            }
          }} 
        />
      )}

      {/* Informações detalhadas */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Última sincronização: {formatLastSync(lastSyncTime)}
        </Typography>
        
        {retryCount > 0 && (
          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
            Tentativas de reconexão: {retryCount}
          </Typography>
        )}
      </Box>

      {/* Botões de ação */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <IconButton
          size="small"
          onClick={() => onForceSync?.()}
          disabled={isSyncing}
          sx={{ 
            bgcolor: alpha(statusColor, 0.1),
            '&:hover': { bgcolor: alpha(statusColor, 0.2) }
          }}
        >
          <CloudDownloadIcon fontSize="small" />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => onReconnect?.()}
          disabled={isSyncing}
          sx={{ 
            bgcolor: alpha(statusColor, 0.1),
            '&:hover': { bgcolor: alpha(statusColor, 0.2) }
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={() => onClearCache?.()}
          sx={{ 
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) }
          }}
        >
          <DeleteSweepIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SyncStatusIndicator; 